import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import NewApp from "./NewApp";
import PrivacyPage from "./pages/PrivacyPage";
import AboutPage from "./pages/AboutPage";
import FaqPage from "./pages/FaqPage";
import ShowPage from "./pages/ShowPage";
import MonthPage from "./pages/MonthPage";
import ArchivePage from "./pages/ArchivePage";
import BestOfPage from "./pages/BestOfPage";
import MostRecommendedPage from "./pages/MostRecommendedPage";
import ShowsLikePage from "./pages/ShowsLikePage";
import MyListPage from "./pages/MyListPage";
import NotFoundPage from "./pages/NotFoundPage";

const routeMap = {
  "/privacy": PrivacyPage,
  "/about": AboutPage,
  "/faq": FaqPage,
};

const collectionRoutePattern = /^\/(?:tonight|this-week|upcoming|genre\/[^/]+|network\/[^/]+)$/;

function normalizePath(pathname) {
  if (!pathname) {
    return "/";
  }
  const trimmed = pathname.replace(/\/+$/, "");
  return trimmed || "/";
}

const AppRouter = ({
  initialReleasesPayload = null,
  initialShowPayload = null,
  initialMonthPayload = null,
  initialArchivePayload = null,
  initialBestOfPayload = null,
  initialMostRecommendedPayload = null,
  initialShowsLikePayload = null,
  initialPath = null,
  initialErrorStatus = null,
}) => {
  const [path, setPath] = useState(() => {
    if (typeof window === "undefined") {
      return normalizePath(initialPath || "/");
    }
    return normalizePath(window.location.pathname);
  });
  // A prerendered 404 or 410 page is served at the address that failed; it shows
  // until the reader moves to another page.
  const [errorPath] = useState(() => (initialErrorStatus ? path : null));

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }
    const handlePopState = () => {
      setPath(normalizePath(window.location.pathname));
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  useEffect(() => {
    if (!collectionRoutePattern.test(path)) {
      return;
    }
    if (typeof window === "undefined") {
      return;
    }
    window.location.replace("/");
  }, [path]);

  if (initialErrorStatus && path === errorPath) {
    return <NotFoundPage status={initialErrorStatus} />;
  }

  const RouteComponent = routeMap[path];
  if (RouteComponent) {
    return <RouteComponent />;
  }

  const showMatch = path.match(/^\/show\/([^/]+)$/);
  if (showMatch) {
    return (
      <ShowPage
        key={showMatch[1]}
        slug={decodeURIComponent(showMatch[1])}
        initialShowPayload={initialShowPayload}
      />
    );
  }

  const monthMatch = path.match(/^\/month\/(\d{4}-\d{2})$/);
  if (monthMatch) {
    return (
      <MonthPage
        key={monthMatch[1]}
        yearMonth={monthMatch[1]}
        initialMonthPayload={initialMonthPayload}
      />
    );
  }

  const bestOfMatch = path.match(/^\/best-of\/(\d{4}-\d{2})$/);
  if (bestOfMatch) {
    return (
      <BestOfPage
        key={bestOfMatch[1]}
        yearMonth={bestOfMatch[1]}
        initialBestOfPayload={initialBestOfPayload}
      />
    );
  }

  const showsLikeMatch = path.match(/^\/shows-like\/([^/]+)$/);
  if (showsLikeMatch) {
    return (
      <ShowsLikePage
        key={showsLikeMatch[1]}
        slug={decodeURIComponent(showsLikeMatch[1])}
        initialShowsLikePayload={initialShowsLikePayload}
      />
    );
  }

  if (path === "/archive") {
    return <ArchivePage initialArchivePayload={initialArchivePayload} />;
  }

  if (path === "/most-recommended") {
    return <MostRecommendedPage initialMostRecommendedPayload={initialMostRecommendedPayload} />;
  }

  if (path === "/my-list") {
    return <MyListPage />;
  }

  if (path === "/") {
    return <NewApp initialReleasesPayload={initialReleasesPayload} />;
  }

  if (collectionRoutePattern.test(path)) {
    return null;
  }

  return <NotFoundPage />;
};

AppRouter.propTypes = {
  initialReleasesPayload: PropTypes.shape({
    data: PropTypes.arrayOf(PropTypes.object),
    referenceDay: PropTypes.string,
    lastUpdated: PropTypes.string,
    count: PropTypes.number,
    totalCount: PropTypes.number,
    candidateCount: PropTypes.number,
    stats: PropTypes.object,
    previousStats: PropTypes.object,
    previousLastUpdated: PropTypes.string,
    previousTotalCount: PropTypes.number,
    monthIndex: PropTypes.shape({
      months: PropTypes.arrayOf(PropTypes.object),
      count: PropTypes.number,
      lastUpdated: PropTypes.string,
    }),
    ifYouLikedPairings: PropTypes.arrayOf(PropTypes.object),
  }),
  initialShowPayload: PropTypes.shape({
    data: PropTypes.object,
    isArchived: PropTypes.bool,
    relatedShows: PropTypes.arrayOf(PropTypes.object),
    curationReasons: PropTypes.arrayOf(PropTypes.string),
    candidateCount: PropTypes.number,
  }),
  initialMonthPayload: PropTypes.shape({
    yearMonth: PropTypes.string,
    label: PropTypes.string,
    data: PropTypes.arrayOf(PropTypes.object),
    ifYouLikedPairings: PropTypes.arrayOf(PropTypes.object),
    count: PropTypes.number,
    previousYearMonth: PropTypes.string,
    nextYearMonth: PropTypes.string,
    totalCount: PropTypes.number,
    candidateCount: PropTypes.number,
    lastUpdated: PropTypes.string,
  }),
  initialArchivePayload: PropTypes.shape({
    months: PropTypes.arrayOf(
      PropTypes.shape({
        yearMonth: PropTypes.string,
        label: PropTypes.string,
        count: PropTypes.number,
        href: PropTypes.string,
        posters: PropTypes.arrayOf(
          PropTypes.shape({
            poster: PropTypes.string,
            title: PropTypes.string,
          })
        ),
      })
    ),
  }),
  initialBestOfPayload: PropTypes.shape({
    yearMonth: PropTypes.string,
    label: PropTypes.string,
    data: PropTypes.arrayOf(PropTypes.object),
    count: PropTypes.number,
    previousYearMonth: PropTypes.string,
    nextYearMonth: PropTypes.string,
    lastUpdated: PropTypes.string,
  }),
  initialMostRecommendedPayload: PropTypes.shape({
    data: PropTypes.arrayOf(PropTypes.object),
    count: PropTypes.number,
    minInbound: PropTypes.number,
    lastUpdated: PropTypes.string,
  }),
  initialShowsLikePayload: PropTypes.shape({
    slug: PropTypes.string,
    anchor: PropTypes.object,
    picks: PropTypes.arrayOf(PropTypes.object),
    inboundContext: PropTypes.shape({
      count: PropTypes.number,
      sources: PropTypes.arrayOf(PropTypes.object),
    }),
    tasteTags: PropTypes.arrayOf(PropTypes.string),
    lastUpdated: PropTypes.string,
  }),
  initialPath: PropTypes.string,
  initialErrorStatus: PropTypes.oneOf([404, 410]),
};

export default AppRouter;
